import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Users, UserPlus, ChevronLeft, ChevronRight, Grid, List, Calendar as CalendarIcon } from 'lucide-react';

const CalendarView = ({ 
  currentDate, 
  sessions, 
  onNavigateMonth, 
  onNavigateWeek,
  onNavigateDay,
  onGoToToday, 
  onEditSession, 
  onManageParticipants, 
  onDeleteSession,
  formatTime,
  getLevelColor,
  getTypeColor,
  getTargetColor
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'

  // Helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getWeekDays = (date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      weekDays.push(day);
    }
    return weekDays;
  };

  const getSessionsForDate = (date) => {
    if (!date) return [];
    
    const dateStr = date.toISOString().split('T')[0];
    return sessions.filter(session => {
      const sessionDate = new Date(session.date).toISOString().split('T')[0];
      return sessionDate === dateStr;
    });
  };

  const getWeeklySessionsForDate = (date) => {
    if (!date) return [];
    
    const dayOfWeek = date.getDay();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[dayOfWeek];
    const dayNameLower = dayName.toLowerCase();
    
    return sessions.filter(session => {
      if (session.type !== 0 || !session.weeklyDays) return false;
      const days = session.weeklyDays.split(',').map((d) => d.trim().toLowerCase()).filter(Boolean);
      return days.includes(dayNameLower);
    });
  };

  const getAllSessionsForDate = (date) => {
    const dailySessions = getSessionsForDate(date);
    const weeklySessions = getWeeklySessionsForDate(date);
    return [...dailySessions, ...weeklySessions];
  };

  const getSessionsForHour = (date, hour) => {
    const allSessions = getAllSessionsForDate(date);
    return allSessions.filter(session => {
      const sessionHour = new Date(`2000-01-01T${session.startTime}`).getHours();
      return sessionHour === hour;
    });
  };

  const getParticipantCountForDate = (session, date) => {
    const dateStr = date.toISOString().split('T')[0];
    if (!session?.participants) return 0;
    return session.participants.filter((p) => {
      if (p.user?.role === 2) return false;
      if (p.participantType === 'Daily' || p.participantType === 'daily') {
        return String(p.bookingDate || '').slice(0, 10) === dateStr;
      }
      return true;
    }).length;
  };

  const generateHours = () => {
    const hours = [];
    for (let i = 6; i <= 22; i++) {
      hours.push(i);
    }
    return hours;
  };

  const handleNavigateWeek = (direction) => {
    onNavigateWeek(direction);
  };

  const handleNavigateDay = (direction) => {
    onNavigateDay(direction);
  };

  const renderMonthView = () => {
    const days = getDaysInMonth(currentDate);
    
    return (
      <div className="grid grid-cols-7 gap-2">
        {days.map((date, index) => {
          if (!date) {
            return <div key={index} className="h-48 border border-gray-200 bg-gray-50"></div>;
          }

          const allSessions = getAllSessionsForDate(date);
          const isToday = date.toDateString() === new Date().toDateString();
            
            return (
              <div
              key={date.toISOString()}
              className={`h-48 border border-gray-200 p-2 ${
                isToday ? 'bg-blue-50 border-blue-200' : 'bg-white'
              } hover:bg-gray-50 transition-colors flex flex-col`}
            >
              {/* Day Header */}
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                <div>
                  <span className={`text-lg font-semibold ${
                    isToday ? 'text-blue-600' : 'text-gray-900'
                  }`}>
                    {date.getDate()}
                  </span>
                  <div className="text-xs text-gray-500">
                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                </div>
                {allSessions.length > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full font-medium">
                    {allSessions.length}
                  </span>
                )}
              </div>

              {/* Scrollable Sessions Area */}
              <div className="flex-1 overflow-y-auto space-y-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {allSessions.length === 0 ? (
                  <div className="text-center text-gray-400 text-xs py-4">
                    {t('sessions.simplified.calendar.noSessions')}
                  </div>
                ) : (
                  allSessions.map((session) => (
                    <div
                      key={session.id}
                      className={`text-xs p-2 rounded cursor-pointer transition-all hover:shadow-sm ${
                        session.type === 0 
                          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200 border border-purple-200' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200'
                      }`}
                      title={`${session.name || 'Session'} - ${session.levelName} - ${session.stableName} - ${formatTime(session.startTime)} - ${formatTime(session.endTime)}`}
                      onClick={() => onEditSession(session)}
                    >
                      {/* Session Header */}
                      <div className="flex items-start justify-between mb-1">
                        <div className="font-medium truncate flex-1">
                          {session.name || t('sessions.simplified.form.sessionName')}
                        </div>
                        <div className={`px-1.5 py-0.5 rounded text-xs font-medium ml-1 ${
                          session.type === 0 
                            ? 'bg-purple-200 text-purple-800' 
                            : 'bg-green-200 text-green-800'
                        }`}>
                          {session.typeName}
                        </div>
                      </div>
                      
                      {/* Session Details */}
                      <div className="space-y-0.5">
                        <div className="flex items-center space-x-1 text-xs opacity-75">
                          <Clock className="w-3 h-3" />
                          <span>{formatTime(session.startTime)} - {formatTime(session.endTime)}</span>
                        </div>
                        
                        <div className="flex items-center space-x-1 text-xs opacity-75">
                          <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                          <span className="truncate">{session.stableName || t('sessions.simplified.form.selectStable')}</span>
                        </div>
                        
                        <div className="flex items-center space-x-1 text-xs opacity-75">
                          <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            session.level === 0 ? 'bg-green-100 text-green-700' :
                            session.level === 1 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {session.levelName || t(`sessions.simplified.form.${session.level === 0 ? 'beginner' : session.level === 1 ? 'intermediate' : 'professional'}`)}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-1 text-xs opacity-75">
                          <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            session.target === 0 ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {session.targetName || t(`sessions.simplified.form.${session.target === 0 ? 'riders' : 'owners'}`)}
                          </span>
                        </div>

                        {(getParticipantCountForDate(session, date) > 0) && (
                          <div className="flex items-center space-x-1 text-xs opacity-75">
                            <Users className="w-3 h-3" />
                            <span>
                              {getParticipantCountForDate(session, date)}
                              {(session.totalCapacity > 0 ? `/${session.totalCapacity}` : session.maxParticipants ? `/${session.maxParticipants}` : '')}{' '}
                              {t('sessions.participants')}
                            </span>
                          </div>
                        )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onManageParticipants(date, session);
                      }}
                      className="mt-1 w-full py-0.5 px-1.5 rounded bg-white/70 hover:bg-white text-[10px] font-medium text-gray-700 border border-gray-200 hover:border-blue-300 flex items-center justify-center gap-1 transition-colors"
                      title="Manage participants for this day"
                    >
                      <UserPlus className="w-2.5 h-2.5" />
                      Reserve
                    </button>
                    </div>
                  ))
                  )}
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays(currentDate);
    
    return (
      <div className="grid grid-cols-7 gap-2">
        {weekDays.map((date, index) => {
          const allSessions = getAllSessionsForDate(date);
          const isToday = date.toDateString() === new Date().toDateString();
            
            return (
            <div
              key={date.toISOString()}
              className={`min-h-48 border border-gray-200 p-3 ${
                isToday ? 'bg-blue-50 border-blue-200' : 'bg-white'
              } hover:bg-gray-50 transition-colors`}
            >
              {/* Day Header */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className={`text-lg font-semibold ${
                    isToday ? 'text-blue-600' : 'text-gray-900'
                  }`}>
                    {date.getDate()}
                  </div>
                  <div className="text-sm text-gray-500">
                    {date.toLocaleDateString('en-US', { weekday: 'long' })}
                  </div>
                </div>
                {allSessions.length > 0 && (
                  <span className="text-sm bg-blue-100 text-blue-600 px-2 py-1 rounded-full font-medium">
                    {allSessions.length} session{allSessions.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>

              {/* Sessions List */}
                <div className="space-y-2">
                {allSessions.length === 0 ? (
                  <div className="text-center text-gray-400 text-sm py-4">
                    No sessions
                  </div>
                ) : (
                  allSessions.map((session) => (
                    <div
                      key={session.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                        session.type === 0 
                          ? 'bg-purple-50 border-purple-200 hover:bg-purple-100' 
                          : 'bg-green-50 border-green-200 hover:bg-green-100'
                      }`}
                      onClick={() => onEditSession(session)}
                    >
                      {/* Session Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="font-semibold text-sm text-gray-900 mb-1">
                            {session.name || 'Session'}
                          </div>
                          <div className="flex items-center space-x-2 text-xs text-gray-600">
                            <Clock className="w-3 h-3" />
                            <span>{formatTime(session.startTime)} - {formatTime(session.endTime)}</span>
                          </div>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                          session.type === 0 
                            ? 'bg-purple-200 text-purple-800' 
                            : 'bg-green-200 text-green-800'
                        }`}>
                          {session.typeName}
                        </div>
                      </div>

                      {/* Session Details */}
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-xs text-gray-600">
                          <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                          <span className="font-medium">Stable:</span>
                          <span>{session.stableName || 'Not specified'}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2 text-xs text-gray-600">
                          <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                          <span className="font-medium">Level:</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            session.level === 0 ? 'bg-green-100 text-green-700' :
                            session.level === 1 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {session.levelName}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-2 text-xs text-gray-600">
                          <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                          <span className="font-medium">Target:</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            session.target === 0 ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {session.targetName}
                          </span>
                        </div>

                        {getParticipantCountForDate(session, date) > 0 && (
                          <div className="flex items-center space-x-2 text-xs text-gray-600">
                            <Users className="w-3 h-3" />
                            <span className="font-medium">Participants:</span>
                            <span>{getParticipantCountForDate(session, date)}</span>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onManageParticipants(date, session);
                        }}
                        className="mt-2 w-full py-1 px-2 rounded bg-white/70 hover:bg-white text-xs font-medium text-gray-700 border border-gray-200 hover:border-blue-300 flex items-center justify-center gap-1 transition-colors"
                        title="Manage participants for this day"
                      >
                        <UserPlus className="w-3 h-3" />
                        Reserve
                      </button>
                    </div>
                  ))
                )}
                </div>
              </div>
            );
          })}
      </div>
    );
  };

  const renderDayView = () => {
    const hours = generateHours();
    
    return (
      <div className="flex-1">
        <div className="grid grid-cols-1 gap-1">
          {hours.map((hour) => {
            const hourSessions = getSessionsForHour(currentDate, hour);
            
            return (
              <div key={hour} className="flex min-h-20 border-b border-gray-200">
                <div className="w-20 flex items-center justify-center text-sm text-gray-500 border-r border-gray-200 bg-gray-50">
                  {hour.toString().padStart(2, '0')}:00
                </div>
                <div className="flex-1 p-3">
                  <div className="space-y-2">
                    {hourSessions.length === 0 ? (
                      <div className="text-gray-300 text-sm">No sessions</div>
                    ) : (
                      hourSessions.map((session) => (
                        <div
                          key={session.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                            session.type === 0 
                              ? 'bg-purple-50 border-purple-200 hover:bg-purple-100' 
                              : 'bg-green-50 border-green-200 hover:bg-green-100'
                          }`}
                          onClick={() => onEditSession(session)}
                        >
                          {/* Session Header */}
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="font-semibold text-sm text-gray-900 mb-1">
                                {session.name || 'Session'}
                              </div>
                              <div className="flex items-center space-x-2 text-xs text-gray-600">
                                <Clock className="w-3 h-3" />
                                <span>{formatTime(session.startTime)} - {formatTime(session.endTime)}</span>
                              </div>
            </div>
                            <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                              session.type === 0 
                                ? 'bg-purple-200 text-purple-800' 
                                : 'bg-green-200 text-green-800'
                            }`}>
                              {session.typeName}
            </div>
          </div>
          
                          {/* Session Details Grid */}
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center space-x-2 text-gray-600">
                              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                              <span className="font-medium">Stable:</span>
                              <span>{session.stableName || 'Not specified'}</span>
              </div>
                            
                            <div className="flex items-center space-x-2 text-gray-600">
                              <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                              <span className="font-medium">Level:</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                session.level === 0 ? 'bg-green-100 text-green-700' :
                                session.level === 1 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {session.levelName}
                        </span>
                      </div>
                            
                            <div className="flex items-center space-x-2 text-gray-600">
                              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                              <span className="font-medium">Target:</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                session.target === 0 ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'
                              }`}>
                                {session.targetName}
                      </span>
                    </div>

                            {getParticipantCountForDate(session, currentDate) > 0 && (
                              <div className="flex items-center space-x-2 text-gray-600">
                                <Users className="w-3 h-3" />
                                <span className="font-medium">Participants:</span>
                                <span>{getParticipantCountForDate(session, currentDate)}</span>
                              </div>
                            )}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageParticipants(currentDate, session);
                    }}
                    className="mt-2 w-full py-1 px-2 rounded bg-white/70 hover:bg-white text-xs font-medium text-gray-700 border border-gray-200 hover:border-blue-300 flex items-center justify-center gap-1 transition-colors"
                    title="Manage participants for this day"
                  >
                    <UserPlus className="w-3 h-3" />
                    Reserve
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
          })}
        </div>
      </div>
    );
  };

  const getNavigationTitle = () => {
    switch (viewMode) {
      case 'month':
        return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      case 'week':
        const weekStart = getWeekDays(currentDate)[0];
        const weekEnd = getWeekDays(currentDate)[6];
        return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      case 'day':
        return currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
      default:
        return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  const handleNavigation = (direction) => {
    switch (viewMode) {
      case 'month':
        onNavigateMonth(direction);
        break;
      case 'week':
        handleNavigateWeek(direction);
        break;
      case 'day':
        handleNavigateDay(direction);
        break;
      default:
        onNavigateMonth(direction);
        break;
    }
  };

  return (
    <div className="p-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {getNavigationTitle()}
        </h2>
        
        <div className="flex items-center space-x-4">
          {/* View Mode Switcher */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'month' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <Grid className="w-4 h-4 inline mr-1" />
              {t('sessions.simplified.monthView')}
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'week' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <List className="w-4 h-4 inline mr-1" />
              {t('sessions.simplified.weekView')}
            </button>
          <button
              onClick={() => setViewMode('day')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'day' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <CalendarIcon className="w-4 h-4 inline mr-1" />
              {t('sessions.simplified.dayView')}
          </button>
          </div>
          
          {/* Navigation Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleNavigation(-1)}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          <button
              onClick={onGoToToday}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
          >
              {t('sessions.simplified.calendar.today')}
          </button>
          <button
              onClick={() => handleNavigation(1)}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
              <ChevronRight className="w-5 h-5" />
          </button>
          </div>
        </div>
      </div>

      {/* Calendar Content */}
      {viewMode === 'month' && (
        <>
          {/* Month Grid Header */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                {day}
              </div>
            ))}
          </div>
          {renderMonthView()}
        </>
      )}

      {viewMode === 'week' && (
        <>
          {/* Week Grid Header */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {getWeekDays(currentDate).map((date) => (
              <div key={date.toISOString()} className="p-2 text-center text-sm font-medium text-gray-500">
                {date.toLocaleDateString('en-US', { weekday: 'short' })}
              </div>
            ))}
          </div>
          {renderWeekView()}
        </>
      )}

      {viewMode === 'day' && (
        <>
          {/* Day View Header */}
          <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="text-lg font-semibold text-gray-900">
                {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </div>
              <div className="text-sm text-gray-500">
                {getAllSessionsForDate(currentDate).length} sessions scheduled
              </div>
            </div>
          </div>
          {renderDayView()}
        </>
      )}

      {/* Legend */}
      <div className="mt-6 flex items-center justify-center space-x-6 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-purple-100 rounded"></div>
          <span className="text-gray-600">{t('sessions.simplified.calendar.legend.weekly')}</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-100 rounded"></div>
          <span className="text-gray-600">{t('sessions.simplified.calendar.legend.daily')}</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;